// Text layout (M14, DRAW_PLAN D7 reduced): one paragraph style per text object, greedy line breaking at
// spaces, left/centre/right, top/middle/bottom, ALL CAPS, tracking and leading, and shrink-to-fit (the size
// steps down until the text fits the box and its line limit; at the smallest size the last line ends in
// "…"). Shaping is opentype.js's: kerning (GPOS/kern) and standard ligatures. Spanish needs no complex
// shaping: á é í ó ú ü ñ ¿ ¡ are single glyphs in every bundled font (unit-tested).
//
// The result is glyph outlines in the node's own coordinates (box from 0, 0), as path commands: the canvas
// draws them as a Path2D and the PDF writer as vector paths, so preview and export are the same shapes.
import type opentype from 'opentype.js';
import type { TextNode } from '../model/types';
import { fontEpoch, getFont, getKern } from './fonts';
import type { KernFn } from './kerning';

export type Cmd =
  | { type: 'M' | 'L'; x: number; y: number }
  | { type: 'Q'; x1: number; y1: number; x: number; y: number }
  | { type: 'C'; x1: number; y1: number; x2: number; y2: number; x: number; y: number }
  | { type: 'Z' };

export type TextLayout = {
  commands: Cmd[];
  /** The size used after shrink-to-fit. */
  size: number;
  lines: string[];
  /** Line boxes (for the editor and tests): x, baseline, width. */
  lineBoxes: { x: number; baseline: number; w: number }[];
  /** Local bounds: the box, or the measured line for point text. */
  bounds: { x: number; y: number; w: number; h: number };
  overflow: boolean;
  /** Glyphs drawn with the font's .notdef (missing characters). */
  missing: number;
};

const FEATURES = { kerning: false, features: { liga: true, rlig: true } };
/** opentype.js 1.3.4 applies the liga/rlig substitutions in stringToGlyphs(s, options); the typings omit it. */
const glyphsOf = (font: opentype.Font, s: string): opentype.Glyph[] =>
  (font.stringToGlyphs as (this: opentype.Font, s: string, o: unknown) => opentype.Glyph[]).call(
    font,
    s,
    FEATURES,
  );

/** Our own positioning: glyphs after ligatures, advance + pair kerning (kerning.ts) + tracking. */
function place(
  font: opentype.Font,
  kern: KernFn,
  s: string,
  size: number,
  tracking: number,
  cb?: (g: opentype.Glyph, x: number) => void,
): number {
  const glyphs = glyphsOf(font, s);
  const k = size / font.unitsPerEm;
  let x = 0;
  for (let i = 0; i < glyphs.length; i++) {
    const g = glyphs[i]!;
    cb?.(g, x);
    x += (g.advanceWidth ?? 0) * k;
    if (i < glyphs.length - 1) x += kern(g.index, glyphs[i + 1]!.index) * k + (tracking / 1000) * size;
  }
  return x;
}

let kernFor: KernFn = () => 0;
function measure(font: opentype.Font, s: string, size: number, tracking: number): number {
  return s ? place(font, kernFor, s, size, tracking) : 0;
}

/** Greedy wrap of one paragraph at spaces; a word wider than the line is put on its own line. */
function wrap(font: opentype.Font, para: string, size: number, tracking: number, width: number): string[] {
  const words = para.split(/ +/).filter((w, i, a) => w || a.length === 1);
  const out: string[] = [];
  let line = '';
  for (const w of words) {
    const cand = line ? `${line} ${w}` : w;
    if (!line || measure(font, cand, size, tracking) <= width) line = cand;
    else {
      out.push(line);
      line = w;
    }
  }
  out.push(line);
  return out;
}

function linesFor(
  font: opentype.Font,
  text: string,
  size: number,
  tracking: number,
  width: number,
): string[] {
  const paras = text.split('\n');
  if (width <= 0) return paras;
  return paras.flatMap((p) => wrap(font, p, size, tracking, width));
}

function fits(font: opentype.Font, lines: string[], size: number, n: TextNode): boolean {
  const { box, style } = n;
  if (box.w > 0 && lines.some((l) => measure(font, l, size, style.tracking) > box.w + 0.01)) return false;
  if (box.maxLines > 0 && lines.length > box.maxLines) return false;
  if (box.h > 0 && textHeight(font, lines.length, size, style.leading) > box.h + 0.01) return false;
  return true;
}

const ascentOf = (font: opentype.Font, size: number) => (font.ascender / font.unitsPerEm) * size;
const descentOf = (font: opentype.Font, size: number) => (-font.descender / font.unitsPerEm) * size;
function textHeight(font: opentype.Font, n: number, size: number, leading: number) {
  return ascentOf(font, size) + descentOf(font, size) + Math.max(0, n - 1) * size * leading;
}

/** Cuts the lines to `max`, ending the last one with "…" that fits the width. */
function ellipsize(
  font: opentype.Font,
  lines: string[],
  max: number,
  size: number,
  tracking: number,
  width: number,
) {
  const out = lines.slice(0, Math.max(1, max));
  if (
    lines.length <= out.length &&
    (width <= 0 || measure(font, out[out.length - 1]!, size, tracking) <= width)
  )
    return out;
  let last = out[out.length - 1]!;
  while (last && width > 0 && measure(font, last + '…', size, tracking) > width)
    last = last.slice(0, -1).trimEnd();
  out[out.length - 1] = last + '…';
  return out;
}

const cache = new WeakMap<TextNode, { epoch: number; layout: TextLayout | null }>();

/** Lays out a text object; null until its font is loaded. Cached per node object. */
export function layoutText(n: TextNode): TextLayout | null {
  const hit = cache.get(n);
  if (hit && hit.epoch === fontEpoch) return hit.layout;
  const layout = compute(n);
  cache.set(n, { epoch: fontEpoch, layout });
  return layout;
}

function compute(n: TextNode): TextLayout | null {
  const { style, box } = n;
  const font = getFont(style.family, style.weight);
  if (!font) return null;
  kernFor = getKern(style.family, style.weight);
  const text = style.caps ? n.text.toLocaleUpperCase('es') : n.text;
  let size = style.size;
  const min = Math.max(1, Math.min(box.minSize || style.size, style.size));
  let lines = linesFor(font, text, size, style.tracking, box.w);
  let overflow = false;
  if (box.w > 0 || box.h > 0) {
    while (!fits(font, lines, size, n) && size > min) {
      size = Math.max(min, size * 0.95);
      lines = linesFor(font, text, size, style.tracking, box.w);
    }
    if (!fits(font, lines, size, n)) {
      overflow = true;
      const byHeight =
        box.h > 0
          ? Math.max(
              1,
              1 + Math.floor((box.h - ascentOf(font, size) - descentOf(font, size)) / (size * style.leading)),
            )
          : Infinity;
      lines = ellipsize(
        font,
        lines,
        Math.min(box.maxLines > 0 ? box.maxLines : Infinity, byHeight),
        size,
        style.tracking,
        box.w,
      );
    }
  }
  const lh = size * style.leading;
  const asc = ascentOf(font, size);
  const H = textHeight(font, lines.length, size, style.leading);
  const y0 =
    box.h > 0 ? (style.valign === 'middle' ? (box.h - H) / 2 : style.valign === 'bottom' ? box.h - H : 0) : 0;
  const commands: Cmd[] = [];
  const lineBoxes: TextLayout['lineBoxes'] = [];
  let missing = 0;
  lines.forEach((line, i) => {
    const w = measure(font, line, size, style.tracking);
    const W = box.w;
    const x =
      style.align === 'left'
        ? 0
        : style.align === 'center'
          ? W > 0
            ? (W - w) / 2
            : -w / 2
          : W > 0
            ? W - w
            : -w;
    const baseline = y0 + asc + i * lh;
    lineBoxes.push({ x, baseline, w });
    place(font, kernFor, line, size, style.tracking, (g, gx) => {
      if (g.index === 0 && g.unicode !== undefined) missing++;
      for (const c of g.getPath(x + gx, baseline, size).commands) commands.push(c as Cmd);
    });
  });
  const maxW = Math.max(0, ...lineBoxes.map((l) => l.w));
  const bounds =
    box.w > 0 || box.h > 0
      ? { x: 0, y: 0, w: box.w > 0 ? box.w : maxW, h: box.h > 0 ? box.h : H }
      : { x: Math.min(...lineBoxes.map((l) => l.x)), y: 0, w: maxW, h: H };
  return { commands, size, lines, lineBoxes, bounds, overflow, missing };
}

/** Path2D from layout commands (canvas). */
export function commandsToPath2D(cmds: Cmd[]): Path2D {
  const p = new Path2D();
  for (const c of cmds)
    if (c.type === 'M') p.moveTo(c.x, c.y);
    else if (c.type === 'L') p.lineTo(c.x, c.y);
    else if (c.type === 'Q') p.quadraticCurveTo(c.x1, c.y1, c.x, c.y);
    else if (c.type === 'C') p.bezierCurveTo(c.x1, c.y1, c.x2, c.y2, c.x, c.y);
    else p.closePath();
  return p;
}
